import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Download, Volume2, AlertCircle, Clock, FileText } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthToken } from '../../utils/authUtils';

interface VisitRecordingPlayerProps {
  encounterId: string;
}

interface RecordingData {
  presigned_url: string;
  visit_id: string;
  visit_date: string | null;
  transcript?: string;
  summary?: string;
}

const VisitRecordingPlayer: React.FC<VisitRecordingPlayerProps> = ({ encounterId }) => {
  const [recording, setRecording] = useState<RecordingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [visitData, setVisitData] = useState<any>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchRecording();
  }, [encounterId]);

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
        audioElementRef.current = null;
      }
    };
  }, []);

  const fetchRecording = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.ENCOUNTERS.BASE}/${encounterId}/visit-recording`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        // No recording found - this is okay, just don't show the player
        setRecording(null);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch recording');
      }

      const data = await response.json();
      setRecording(data);
      
      // Fetch visit data for transcript and summary
      if (data.visit_id) {
        try {
          const visitResponse = await fetch(`${API_ENDPOINTS.VISIT_TRANSCRIPTS.GET(data.visit_id)}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (visitResponse.ok) {
            const visitData = await visitResponse.json();
            setVisitData(visitData);
          }
        } catch (err) {
          console.error('Failed to fetch visit data:', err);
        }
      }
    } catch (err: any) {
      console.error('Error fetching recording:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAudio = async () => {
    if (!recording) return;

    // If audio is already playing, pause it
    if (audioElementRef.current && isPlaying) {
      try {
        audioElementRef.current.pause();
        setIsPlaying(false);
        return;
      } catch (e) {
        console.error('Pause failed:', e);
      }
      return;
    }

    // If audio element exists but not playing, play it
    if (audioElementRef.current && !isPlaying) {
      try {
        await audioElementRef.current.play();
        setIsPlaying(true);
        return;
      } catch (playError) {
        console.error('Play failed:', playError);
        // If play fails, fetch new presigned URL
        audioElementRef.current = null;
      }
    }

    // Create new audio element with fresh presigned URL
    try {
      setAudioLoading(true);
      setAudioError(null);
      
      // Fetch fresh presigned URL
      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.ENCOUNTERS.BASE}/${encounterId}/visit-recording`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get audio access: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.presigned_url) {
        throw new Error('Invalid audio access response - no presigned URL');
      }

      const audio = new Audio();
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
      });
      
      audio.addEventListener('error', (e) => {
        console.error('Audio error event:', e);
        
        let errorMessage = 'Failed to load audio file';
        
        if (audio.error) {
          switch (audio.error.code) {
            case MediaError.MEDIA_ERR_NETWORK:
              errorMessage = 'Network error while loading audio. The URL may have expired. Click Play again to get a fresh URL.';
              audioElementRef.current = null;
              break;
            case MediaError.MEDIA_ERR_DECODE:
              errorMessage = 'Audio file is corrupted or malformed.';
              break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
              errorMessage = 'Audio format not supported by this browser.';
              break;
            default:
              errorMessage = `Audio error: ${audio.error.message || 'Unknown error'}`;
          }
        }
        
        setAudioError(errorMessage);
        setIsPlaying(false);
        setAudioLoading(false);
      });

      const canPlayM4A = audio.canPlayType('audio/mp4; codecs="mp4a.40.2"');
      const canPlayM4AAlt = audio.canPlayType('audio/m4a');
      
      if (!canPlayM4A && !canPlayM4AAlt) {
        throw new Error('Your browser does not support M4A audio files.');
      }
      
      audio.src = data.presigned_url;
      audio.load();
      
      audioElementRef.current = audio;
      
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (playError: any) {
        console.error('Play failed:', playError);
        throw new Error(`Playback failed: ${playError.message}`);
      }
    } catch (err: any) {
      console.error('Error playing audio:', err);
      setAudioError(err.message);
    } finally {
      setAudioLoading(false);
    }
  };

  const downloadAudio = async () => {
    if (!recording) return;
    
    try {
      const response = await fetch(recording.presigned_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visit-recording-${recording.visit_id}.m4a`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download recording');
    }
  };

  // Don't show anything while loading or if no recording
  if (loading) return null;
  if (!recording) return null;
  if (error) return null;

  return (
    <div className="p-8">
      <h2 className="text-2xl font-semibold mb-4">Visit Recording</h2>
      
      {/* Audio Player */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Volume2 className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">Audio Recording</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleAudio}
              disabled={audioLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors duration-200"
            >
              {audioLoading ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : isPlaying ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Play
                </>
              )}
            </button>
            
            <button
              onClick={downloadAudio}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors duration-200"
              title="Download audio file"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
          </div>
        </div>
        
        {audioError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-red-700 text-sm">{audioError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Transcript */}
      {visitData?.full_text && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <FileText className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Full Transcript</h3>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed max-h-96 overflow-y-auto">
              {visitData.full_text}
            </pre>
          </div>
        </div>
      )}

      {/* Summary */}
      {visitData?.summary && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <FileText className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Summary</h3>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
              {visitData.summary}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitRecordingPlayer;
